import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin, Quote, BadgeCheck, User } from "lucide-react";
import {
  COMPANY,
  CEO,
  MISSION_VISION,
  VALUES,
  DIFFERENTIATORS,
  STATS,
  MEDIA,
  TEAM,
  CERTIFICATIONS,
  INDUSTRIES,
  PARTNERS_NETWORK,
  CLIENTS,
} from "@/data/content";
import { Reveal } from "@/components/Reveal";
import { BrandLogo } from "@/components/BrandLogo";

const initials = (name) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();

const PersonCard = ({ p, testid }) => (
  <Reveal className="bento-card p-7 h-full" data-testid={testid}>
    <div className="flex items-start gap-4 mb-5">
      <div className="w-14 h-14 flex-shrink-0 flex items-center justify-center bg-[#FAF7F2] border border-stone-200 font-display text-lg text-[#B8860B]">
        {initials(p.name)}
      </div>
      <div>
        <div className="font-display text-lg text-stone-900 leading-tight">{p.name}</div>
        <div className="text-xs uppercase tracking-[0.18em] text-[#B8860B] mt-1.5">{p.role}</div>
      </div>
    </div>
    <p className="text-sm text-stone-600 leading-relaxed">{p.bio}</p>
  </Reveal>
);

const About = () => {
  useEffect(() => window.scrollTo({ top: 0 }), []);

  return (
    <main data-testid="page-about">
      {/* Hero */}
      <section className="relative pt-40 pb-24 overflow-hidden border-b border-stone-200">
        <img src={MEDIA.dubai} alt="Dubai" className="absolute inset-0 w-full h-full object-cover opacity-50 pencil" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#FAF7F2] via-[#FAF7F2]/60 to-[#FAF7F2]" />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative">
          <Reveal>
            <div className="text-xs uppercase tracking-[0.25em] text-[#B8860B] font-semibold mb-6">
              About ITHR Technologies
            </div>
            <h1 className="font-display text-stone-900 text-5xl md:text-7xl lg:text-8xl font-light tracking-tighter mb-10 max-w-5xl leading-[1.02]">
              A Dubai-born consulting<br />
              house, blending HR &amp;<br />
              <span className="italic text-[#B8860B]">technology.</span>
            </h1>
            <div className="flex items-center gap-3 text-stone-600">
              <MapPin className="w-4 h-4 text-[#B8860B]" />
              <span>Headquartered in {COMPANY.hq} · founded {COMPANY.founded} · {COMPANY.tagline_secondary}</span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Mission + Vision */}
      <section className="py-24 md:py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.25em] text-[#B8860B] font-semibold mb-4">
              Mission &amp; Vision
            </div>
            <h2 className="font-display text-stone-900 text-4xl md:text-5xl font-light tracking-tighter sticky top-32">
              Humanize the systems<br />that run the world.
            </h2>
          </Reveal>
          <Reveal className="lg:col-span-7 space-y-8">
            <div className="border border-stone-200 p-8 bg-white">
              <div className="font-mono text-[#B8860B] text-xs mb-3 uppercase tracking-[0.25em]">Vision</div>
              <p className="text-stone-700 text-lg leading-relaxed">{MISSION_VISION.vision}</p>
            </div>
            <div className="border border-stone-200 p-8 bg-white">
              <div className="font-mono text-[#B8860B] text-xs mb-3 uppercase tracking-[0.25em]">Mission</div>
              <p className="text-stone-700 text-lg leading-relaxed">{MISSION_VISION.mission}</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CEO message */}
      <section className="py-24 md:py-28 border-y border-stone-200 bg-[#F5F1EA]">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
          <Reveal>
            <Quote className="w-14 h-14 text-[#B8860B] mb-8" strokeWidth={1} />
            <div className="text-xs uppercase tracking-[0.25em] text-[#B8860B] font-semibold mb-4">
              Message from our CEO
            </div>
            <p className="font-display text-stone-900 text-2xl md:text-3xl lg:text-4xl font-light leading-snug tracking-tight mb-8">
              "{CEO.message}"
            </p>
            <p className="text-stone-700 text-lg leading-relaxed mb-10 max-w-3xl">
              {CEO.closer}
            </p>
            <div className="flex items-center gap-4 pt-6 border-t border-stone-300">
              <div className="w-14 h-14 flex items-center justify-center bg-white border border-stone-300 font-display text-xl text-[#B8860B]">
                {initials(CEO.name)}
              </div>
              <div>
                <div className="font-display text-xl text-stone-900">{CEO.name}</div>
                <div className="text-sm text-stone-600">{CEO.title} · {COMPANY.name}</div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Stats band */}
      <section className="border-b border-stone-200 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-2 md:grid-cols-4">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 80} className={`py-10 md:py-16 ${i !== STATS.length - 1 ? "md:border-r" : ""} border-stone-200`}>
              <div className="font-display text-5xl md:text-6xl text-stone-900 font-light tracking-tighter">{s.value}</div>
              <div className="text-xs uppercase tracking-[0.25em] text-stone-500 mt-3">{s.label}</div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Leadership Team */}
      <section className="py-24 md:py-32" id="team">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="text-xs uppercase tracking-[0.25em] text-[#B8860B] font-semibold mb-4">
              Our team
            </div>
            <h2 className="font-display text-stone-900 text-4xl md:text-5xl lg:text-6xl font-light tracking-tighter mb-6 max-w-3xl">
              Senior specialists,<br />delivering side by side.
            </h2>
            <p className="text-stone-600 text-lg max-w-2xl mb-16 leading-relaxed">
              A senior bench of SAP, AI, GRC, HR and talent specialists — combining 150+ man-years of expertise across UAE, GCC, India and beyond.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {TEAM.map((p, i) => (
              <PersonCard key={p.name} p={p} testid={`team-card-${i}`} />
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-24 md:py-32" id="certifications">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="text-xs uppercase tracking-[0.25em] text-[#B8860B] font-semibold mb-4">
              Certifications &amp; Awards
            </div>
            <h2 className="font-display text-stone-900 text-4xl md:text-5xl lg:text-6xl font-light tracking-tighter mb-16 max-w-3xl">
              Quality, compliance &amp;<br />recognition you can verify.
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-stone-200 border border-stone-200">
            {CERTIFICATIONS.map((c, i) => (
              <Reveal key={c.code} delay={i * 80} className="bg-white p-8 lg:p-10" data-testid={`certification-${i}`}>
                <BadgeCheck className="w-10 h-10 text-[#B8860B] mb-6" strokeWidth={1.25} />
                <div className="font-mono text-stone-900 text-base font-medium mb-2">{c.code}</div>
                <div className="font-display text-lg text-stone-900 mb-3">{c.label}</div>
                <p className="text-sm text-stone-500 leading-relaxed">{c.detail}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Industries served */}
      <section className="py-20 md:py-28 border-t border-stone-200 bg-[#F5F1EA]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
              <div className="lg:col-span-4">
                <div className="text-xs uppercase tracking-[0.25em] text-[#B8860B] font-semibold mb-4">
                  Industries we serve
                </div>
                <h2 className="font-display text-stone-900 text-3xl md:text-4xl font-light tracking-tighter">
                  Deep sector experience<br />across the Middle East.
                </h2>
              </div>
              <div className="lg:col-span-8 flex flex-wrap gap-3" data-testid="industries-list">
                {INDUSTRIES.map((ind) => (
                  <span key={ind} className="inline-flex items-center px-4 py-2 bg-white border border-stone-200 text-stone-700 text-sm">
                    {ind}
                  </span>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Clients logo wall */}
      <section className="py-24 md:py-28 border-t border-stone-200 bg-white" id="clients">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="text-xs uppercase tracking-[0.25em] text-[#B8860B] font-semibold mb-4">
              In us they trust
            </div>
            <h2 className="font-display text-stone-900 text-3xl md:text-4xl lg:text-5xl font-light tracking-tighter mb-12 max-w-3xl">
              Our clients.
            </h2>
          </Reveal>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-px bg-stone-200 border border-stone-200">
            {CLIENTS.map((c, i) => (
              <Reveal key={c.name} delay={i * 30} className="bg-white h-24 flex items-center justify-center px-4" data-testid={`client-logo-${i}`}>
                <BrandLogo name={c.name} domain={c.domain} size={36} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Partner Network */}
      <section className="py-24 md:py-28 border-t border-stone-200">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="text-xs uppercase tracking-[0.25em] text-[#B8860B] font-semibold mb-4">
              Partner network
            </div>
            <h2 className="font-display text-stone-900 text-3xl md:text-4xl lg:text-5xl font-light tracking-tighter mb-12 max-w-3xl">
              We collaborate to deliver.
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-stone-200 border border-stone-200">
            {PARTNERS_NETWORK.map((p, i) => (
              <Reveal key={p.name} delay={i * 50} className="bg-white p-6 md:p-7 flex items-center gap-5 min-h-[112px]" data-testid={`partner-${i}`}>
                <BrandLogo name={p.name} domain={p.domain} size={40} showName={false} />
                <div className="border-l border-stone-200 pl-5">
                  <div className="font-display text-lg text-stone-900">{p.name}</div>
                  <div className="text-xs uppercase tracking-[0.18em] text-stone-500 mt-1">{p.role}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 md:py-28 border-t border-stone-200 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="text-xs uppercase tracking-[0.25em] text-[#B8860B] font-semibold mb-4">
              Our values
            </div>
            <h2 className="font-display text-stone-900 text-4xl md:text-5xl lg:text-6xl font-light tracking-tighter mb-16 max-w-3xl">
              Four principles that<br />govern every engagement.
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-stone-200 border border-stone-200">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 80} className="bg-white p-8 lg:p-10" data-testid={`value-${i}`}>
                <div className="font-mono text-[#B8860B] text-sm mb-6">0{i + 1}</div>
                <h3 className="font-display text-2xl text-stone-900 mb-3 font-medium">{v.title}</h3>
                <p className="text-stone-600 leading-relaxed">{v.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Differentiators */}
      <section className="py-24 md:py-32 border-t border-stone-200">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal>
            <h2 className="font-display text-stone-900 text-4xl md:text-5xl lg:text-6xl font-light tracking-tighter mb-16 max-w-4xl">
              Why partners choose us.
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {DIFFERENTIATORS.map((d, i) => (
              <Reveal key={d.title} delay={i * 100} className="bento-card p-10">
                <d.icon className="w-10 h-10 text-[#B8860B] mb-6" strokeWidth={1.25} />
                <h3 className="font-display text-2xl text-stone-900 mb-4">{d.title}</h3>
                <p className="text-stone-600 leading-relaxed">{d.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 border-t border-stone-200 text-center">
        <Reveal className="max-w-3xl mx-auto px-6">
          <p className="text-stone-600 mb-8 text-lg">Want to know more, or visit our Dubai office?</p>
          <Link to="/contact" data-testid="about-contact-cta" className="btn-primary">
            Get in touch <ArrowUpRight className="w-4 h-4" />
          </Link>
        </Reveal>
      </section>
    </main>
  );
};

export default About;
